import React, { useEffect, useMemo, useRef, useState } from "react";

type Product = { id?: string; code?: string; title?: string; name?: string; benefit?: string; url: string; image?: string; price?: string; };
type Sign = { id: string; label: string; emoji?: string; element?: string; dates?: string; description?: string; };
type Deluxe = {
  email_subject: string;
  teaser: string;
  attraction: string;
  glue: string;
  friction: string;
  conflict: string;
  intimacy: string;
  ritual_7days: string[];
  valentines_tip: string;
};
type Compatibility = {
  key: string;
  compatibility_score: number;
  title: string;
  description: string;
  strengths: string[];
  challenges: string[];
  love_tip: string;
  deluxe?: Deluxe;
};
type Coupon = { code: string; description: string; };
type Config = { campaign?: { title?: string; headline?: string; subheadline?: string; }; ui: Record<string, unknown>; signs: Record<string, Sign>; };
type CalcResult = { partner1_sign: Sign; partner2_sign: Sign; compatibility: Compatibility; products: Product[]; coupon: Coupon; };
type InputMode = "birthdate" | "sign";

const VARIATION_BY_PRODUCT_CODE: Record<string, string> = {
  "41314/01": "167",
  "41328/01": "170"
};

const VARIATION_BY_PRODUCT_ID: Record<string, string> = {
  "144": "167",
  "3162": "170"
};

const SIGN_ORDER = [
  "aries",
  "taurus",
  "gemini",
  "cancer",
  "leo",
  "virgo",
  "libra",
  "scorpio",
  "sagittarius",
  "capricorn",
  "aquarius",
  "pisces"
];

const SIGN_BIRTHDATE: Record<string, string> = {
  aries: "1990-03-25",
  taurus: "1990-04-25",
  gemini: "1990-05-25",
  cancer: "1990-06-25",
  leo: "1990-07-25",
  virgo: "1990-08-25",
  libra: "1990-09-25",
  scorpio: "1990-10-25",
  sagittarius: "1990-11-25",
  capricorn: "1990-01-10",
  aquarius: "1990-02-01",
  pisces: "1990-03-05"
};

const getVariationIdForProduct = (product: Product) => {
  if (product.code && VARIATION_BY_PRODUCT_CODE[product.code]) return VARIATION_BY_PRODUCT_CODE[product.code];
  if (product.id && VARIATION_BY_PRODUCT_ID[product.id]) return VARIATION_BY_PRODUCT_ID[product.id];
  return undefined;
};

const padTwo = (value: number) => value.toString().padStart(2, "0");

const normalizeCzDateInput = (value: string) => {
  const normalized = value.replace(/[\\/,-]/g, ".").replace(/[^\d.]/g, "");
  if (normalized.includes(".")) {
    const endsWithDot = normalized.endsWith(".");
    const rawParts = normalized.split(".").filter((part) => part !== "");
    const parts = rawParts.slice(0, 3).map((part, index) => {
      const limit = index === 2 ? 4 : 2;
      return part.replace(/\D/g, "").slice(0, limit);
    });
    let output = parts.join(".");
    if (endsWithDot && output.split(".").length < 3) {
      output += ".";
    }
    return output;
  }
  const digits = normalized.replace(/\D/g, "").slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  const output: string[] = [];
  if (day) output.push(day);
  if (month) output.push(month);
  if (year) output.push(year);
  return output.join(".");
};

const parseCzDateInput = (value: string) => {
  const match = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (!day || !month || !year) return null;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return { date, iso: `${year}-${padTwo(month)}-${padTwo(day)}` };
};

const Hearts = () => (
  <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
    {Array.from({ length: 15 }).map((_, index) => (
      <div
        key={`heart-${index}`}
        className="floating-heart"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100 + 20}%`,
          animationDelay: `${Math.random() * 5}s`,
          fontSize: `${Math.random() * 16 + 12}px`,
          opacity: Math.random() * 0.5 + 0.3
        }}
      >
        {index % 3 === 0 ? "💕" : index % 3 === 1 ? "❤️" : "💖"}
      </div>
    ))}
  </div>
);

const CONFETTI_COLORS = ["#ff007d", "#ff69b4", "#ff1493", "#dc143c", "#ff6b6b"];

const Confetti = () => {
  const pieces = useMemo(
    () => Array.from({ length: 36 }).map(() => ({
      left: Math.random() * 100,
      size: Math.random() * 6 + 6,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      delay: Math.random() * 250,
      duration: Math.random() * 700 + 1200,
      rotate: Math.random() * 360
    })),
    []
  );

  return (
    <div className="confetti-layer" aria-hidden="true">
      {pieces.map((piece, index) => {
        const style = {
          left: `${piece.left}%`,
          width: `${piece.size}px`,
          height: `${Math.max(4, piece.size * 0.4)}px`,
          background: piece.color,
          animationDelay: `${piece.delay}ms`,
          animationDuration: `${piece.duration}ms`,
          "--rotate": `${piece.rotate}deg`
        } as React.CSSProperties;
        return <span key={`confetti-${index}`} className="confetti-piece" style={style} />;
      })}
    </div>
  );
};

export default function Widget({ apiBase }: { apiBase?: string }) {
  const [config, setConfig] = useState<Config | null>(null);
  const [partner1Birthdate, setPartner1Birthdate] = useState("");
  const [partner2Birthdate, setPartner2Birthdate] = useState("");
  const [partner1Mode, setPartner1Mode] = useState<InputMode>("sign");
  const [partner2Mode, setPartner2Mode] = useState<InputMode>("sign");
  const [partner1Sign, setPartner1Sign] = useState("");
  const [partner2Sign, setPartner2Sign] = useState("");
  const [partner1Error, setPartner1Error] = useState<string | null>(null);
  const [partner2Error, setPartner2Error] = useState<string | null>(null);
  const [useTextDateInput] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [result, setResult] = useState<CalcResult | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isAddingSet, setIsAddingSet] = useState(false);
  const [cartStatus, setCartStatus] = useState<"idle" | "adding" | "added" | "error">("idle");
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [submittedBirthdate1, setSubmittedBirthdate1] = useState<string | null>(null);
  const [submittedBirthdate2, setSubmittedBirthdate2] = useState<string | null>(null);
  const copyTimeoutRef = useRef<number | null>(null);
  const toastTimeoutRef = useRef<number | null>(null);
  const confettiTimeoutRef = useRef<number | null>(null);
  const confettiResultRef = useRef<string | null>(null);

  useEffect(() => {
    fetch(`${apiBase || ""}/api/horoscope/config`).then(r => r.json()).then(setConfig).catch(console.error);
  }, [apiBase]);


  const ui = config?.ui || {};
  const requiredDateMessage = (ui.error_required_date as string) || "Zadejte datum narození.";
  const requiredSignMessage = (ui.error_required_sign as string) || "Zvolte znamení.";
  const invalidTextDateMessage = (ui.error_invalid_date as string) || "Zadejte datum ve formátu DD.MM.RRRR.";
  const futureDateMessage = (ui.error_future_date as string) || "Datum nemůže být v budoucnosti.";

  const signOptions = useMemo(() => {
    const signs = config?.signs || {};
    const orderIndex = new Map(SIGN_ORDER.map((id, index) => [id, index]));
    return Object.entries(signs)
      .sort((a, b) => (orderIndex.get(a[0]) ?? 999) - (orderIndex.get(b[0]) ?? 999))
      .map(([id, payload]) => ({
        id,
        label: payload.label || id,
        emoji: payload.emoji || ""
      }));
  }, [config]);

  const resolveBirthdateForSubmit = (
    mode: InputMode,
    value: string,
    signId: string,
    setError: (e: string | null) => void
  ) => {
    if (mode === "sign") {
      if (!signId) {
        setError(requiredSignMessage);
        return null;
      }
      const mapped = SIGN_BIRTHDATE[signId];
      if (!mapped) {
        setError(requiredSignMessage);
        return null;
      }
      return mapped;
    }

    if (!value) {
      setError(requiredDateMessage);
      return null;
    }
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (useTextDateInput) {
      const parsed = parseCzDateInput(value);
      if (!parsed) {
        setError(invalidTextDateMessage);
        return null;
      }
      if (parsed.date > todayStart) {
        setError(futureDateMessage);
        return null;
      }
      return parsed.iso;
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      setError(invalidTextDateMessage);
      return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime()) || date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      setError(invalidTextDateMessage);
      return null;
    }
    if (date > todayStart) {
      setError(futureDateMessage);
      return null;
    }
    return value;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPartner1Error(null);
    setPartner2Error(null);

    const resolved1 = resolveBirthdateForSubmit(
      partner1Mode,
      partner1Birthdate,
      partner1Sign,
      setPartner1Error
    );
    const resolved2 = resolveBirthdateForSubmit(
      partner2Mode,
      partner2Birthdate,
      partner2Sign,
      setPartner2Error
    );

    if (!resolved1 || !resolved2) return;

    setResult(null);
    setIsLoading(true);
    setIsPreparing(true);
    setCartStatus("idle");
    setEmailStatus("idle");
    setSubmittedBirthdate1(resolved1);
    setSubmittedBirthdate2(resolved2);

    try {
      const responsePromise = fetch(`${apiBase || ""}/api/horoscope/calc`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          partner1: { birthdate: resolved1, gender: "female" },
          partner2: { birthdate: resolved2, gender: "male" }
        })
      });
      const delay = new Promise((resolve) => setTimeout(resolve, 1200));
      const [r] = await Promise.all([responsePromise, delay]);
      const data = await r.json();
      setResult(data);
    } finally {
      setIsLoading(false);
      setIsPreparing(false);
    }
  };

  const handleDateValueChange = (value: string, setter: (v: string) => void, errorSetter: (e: string | null) => void) => {
    setter(value);
    errorSetter(null);
  };

  const handleTextDateChange = (event: React.ChangeEvent<HTMLInputElement>, setter: (v: string) => void, errorSetter: (e: string | null) => void) => {
    handleDateValueChange(normalizeCzDateInput(event.target.value), setter, errorSetter);
  };

  const handleCopy = async () => {
    if (!result?.coupon?.code) return;
    try {
      await navigator.clipboard.writeText(result.coupon.code);
    } catch {
      const temp = document.createElement("textarea");
      temp.value = result.coupon.code;
      document.body.appendChild(temp);
      temp.select();
      document.execCommand("copy");
      document.body.removeChild(temp);
    }
    setCopyState("copied");
    setShowCopyToast(true);
    if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
    if (toastTimeoutRef.current) window.clearTimeout(toastTimeoutRef.current);
    copyTimeoutRef.current = window.setTimeout(() => setCopyState("idle"), 1500);
    toastTimeoutRef.current = window.setTimeout(() => setShowCopyToast(false), 2000);
  };

  const share = (platform: "whatsapp" | "facebook") => {
    if (!result) return;
    const score = result.compatibility.compatibility_score;
    const template = ui.share_text as string | undefined;
    const text = (template ? template.replace("{score}", String(score)) : `S partnerem jsme kompatibilní na ${score}%! Zjisti i ty svůj partnerský horoskop.`).trim();
    const shareUrl = window.location.href;
    if (platform === "whatsapp") {
      const message = encodeURIComponent(`${text}\n${shareUrl}`);
      window.open(`https://wa.me/?text=${message}`, "_blank", "noopener,noreferrer");
    } else {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");
    }
  };

  const addToCartRequest = async (productId: string, variationId?: string) => {
    const params = new URLSearchParams({
      act: "add",
      redir: "0",
      IDproduct: productId,
      No: "1"
    });
    if (variationId) {
      params.set("IDvariation", variationId);
    }
    await fetch(`/kosik/?${params.toString()}`, { credentials: "include" });
  };

  const resolveAssetUrl = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
    if (url.startsWith("//")) return `${window.location.protocol}${url}`;
    if (url.startsWith("/") && apiBase) return `${apiBase}${url}`;
    return url;
  };

  const handleBuySet = async () => {
    if (!result || isAddingSet || cartStatus === "added") return;
    const products = result.products?.slice(0, 3) || [];
    const entries = products.map((p) => ({ id: p.id, variationId: getVariationIdForProduct(p) }));
    if (entries.some((entry) => !entry.id)) {
      window.open("https://www.kralovstvi-tiande.cz/valentyn/", "_blank", "noopener,noreferrer");
      return;
    }

    setIsAddingSet(true);
    setCartStatus("adding");
    try {
      const helper = (window as typeof window & {
        TiandeBasketHelper?: { addToBasket: (productId: string) => Promise<boolean> | boolean };
      }).TiandeBasketHelper;

      for (const entry of entries) {
        const id = entry.id as string;
        if (helper?.addToBasket && !entry.variationId) {
          await helper.addToBasket(id);
        } else {
          await addToCartRequest(id, entry.variationId);
        }
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      setCartStatus("added");
    } catch (error) {
      console.error("Add set to cart failed", error);
      setCartStatus("error");
    } finally {
      setIsAddingSet(false);
    }
  };

  const cartUrl = (() => {
    const base = "/kosik/";
    const couponParam = "discountCode";
    const couponCode = result?.coupon?.code;
    if (!couponCode) return base;
    const params = new URLSearchParams({ [couponParam]: couponCode });
    return `${base}?${params.toString()}`;
  })();

  const handleOpenCart = () => {
    window.open(cartUrl, "_blank", "noopener,noreferrer");
  };

  const handleCloseWidget = () => {
    setIsOpen(false);
    window.location.href = "/";
  };

  const handleSendEmail = async () => {
    if (!email || !result || emailStatus === "sent") return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return;

    const resolvedBirthdate1 = submittedBirthdate1 || partner1Birthdate;
    const resolvedBirthdate2 = submittedBirthdate2 || partner2Birthdate;

    setEmailStatus("sending");
    try {
      const response = await fetch(`${apiBase || ""}/api/horoscope/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          partner1_sign: result.partner1_sign.id,
          partner2_sign: result.partner2_sign.id,
          partner1_birthdate: resolvedBirthdate1,
          partner2_birthdate: resolvedBirthdate2,
          partner1_birthdate_provided: partner1Mode === "birthdate",
          partner2_birthdate_provided: partner2Mode === "birthdate"
        })
      });
      const data = await response.json();
      if (data.success) {
        setEmailStatus("sent");
      } else {
        setEmailStatus("error");
      }
    } catch {
      setEmailStatus("error");
    }
  };

  const headlineText = (ui.headline as string) || config?.campaign?.headline || "Zjistěte, jak k sobě ladíte";
  const introSubtitle = (config?.campaign?.subheadline as string) || "";
  const partner1Label = (ui.partner1_label as string) || "Partner 1";
  const partner2Label = (ui.partner2_label as string) || "Partner 2";
  const birthdateLabel = (ui.birthdate_label as string) || "Datum narození";
  const genderLabel = (ui.gender_label as string) || "Pohlaví";
  const genderMale = (ui.gender_male as string) || "Muž";
  const genderFemale = (ui.gender_female as string) || "Žena";
  const signLabel = (ui.sign_label as string) || "Znamení";
  const signPlaceholder = (ui.sign_placeholder as string) || "Vyberte znamení";
  const modeBirthdateLabel = (ui.mode_birthdate as string) || "Datum narození";
  const modeSignLabel = (ui.mode_sign as string) || "Znamení";
  const ctaSubmitLabel = (ui.cta_submit as string) || "Zjistit kompatibilitu";
  const ctaSubmitLoadingLabel = (ui.cta_submit_loading as string) || "Počítám...";
  const launchButtonLabel = (ui.launch_button as string) || "Zjistit partnerský horoskop";
  const prepTitle = (ui.prep_title as string) || "Připravuji váš partnerský horoskop...";
  const prepSubtitle = (ui.prep_subtitle as string) || "Spojuji hvězdy pro váš pár.";
  const resultHeadline = (ui.result_headline as string) || "Váš partnerský horoskop";
  const benefitsLabel = (ui.benefits_label as string) || "Silné stránky vztahu:";
  const challengesLabel = (ui.challenges_label as string) || "Výzvy:";
  const loveTipLabel = (ui.love_tip_label as string) || "Tip pro lásku:";
  const productsLabel = (ui.products_label as string) || "Doporučené valentýnské produkty:";
  const couponLabel = (ui.coupon_label as string) || "Valentýnská sleva";
  const couponBenefit = (ui.coupon_benefit as string) || "";
  const ctaCopyLabel = (ui.cta_copy as string) || "Zkopírovat kód";
  const shareLabel = (ui.share_label as string) || "Sdílej:";
  const ctaBuyLabel = (ui.cta_buy as string) || "Koupit valentýnský set";
  const cartAddingLabel = (ui.cart_adding as string) || "Přidávám do košíku...";
  const cartAddedLabel = (ui.cart_added as string) || "Přidáno do košíku";
  const cartErrorLabel = (ui.cart_error as string) || "Nepodařilo se přidat. Zkuste to znovu.";
  const openCartLabel = (ui.open_cart as string) || "Otevřít košík";
  const copyToastLabel = (ui.copy_toast as string) || "Kód zkopírován!";
  const closeTooltipLabel = (ui.close_tooltip as string) || "Výsledek si můžete kdykoliv znovu zobrazit.";
  const inputHint = (ui.input_hint as string) || "(např. 14.2.1990)";
  const previewNote = (ui.preview_note as string)
    || "Toto je jen ochutnávka. Celý horoskop včetně 7denního rituálu a tipů pro intimitu vám pošleme na email.";
  const emailLabel = (ui.email_label as string) || "Celý horoskop vám pošleme na email";
  const emailPlaceholder = (ui.email_placeholder as string) || "Váš email";
  const emailSubmitLabel = (ui.email_submit as string) || "Odeslat na email";
  const emailSuccessLabel = (ui.email_success as string) || "Horoskop jsme poslali do emailu. Děkujeme!";
  const emailErrorLabel = (ui.email_error as string) || "Nepodařilo se odeslat. Zkuste to znovu.";

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current);
      if (toastTimeoutRef.current) window.clearTimeout(toastTimeoutRef.current);
      if (confettiTimeoutRef.current) window.clearTimeout(confettiTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!result || isPreparing) return;
    const resultKey = result.compatibility.key;
    if (confettiResultRef.current === resultKey) return;
    confettiResultRef.current = resultKey;
    setShowConfetti(true);
    if (confettiTimeoutRef.current) window.clearTimeout(confettiTimeoutRef.current);
    confettiTimeoutRef.current = window.setTimeout(() => setShowConfetti(false), 2200);
  }, [result, isPreparing]);

  if (!isOpen) return (
    <button
      className="valentine-launch"
      onClick={() => setIsOpen(true)}
    >
      💕 {launchButtonLabel}
    </button>
  );

  const renderDateInput = (
    value: string,
    setValue: (v: string) => void,
    error: string | null,
    setError: (e: string | null) => void
  ) => {
    if (useTextDateInput) {
      return (
        <input
          type="text"
          inputMode="decimal"
          autoComplete="bday"
          placeholder="DD.MM.RRRR"
          value={value}
          onChange={(e) => handleTextDateChange(e, setValue, setError)}
          required
          maxLength={10}
          className={`valentine-date-input${error ? " is-error" : ""}`}
          aria-invalid={Boolean(error)}
        />
      );
    }
    return (
      <input
        type="date"
        value={value}
        onChange={(e) => handleDateValueChange(e.target.value, setValue, setError)}
        required
        autoComplete="bday"
        className={`valentine-date-input${error ? " is-error" : ""}`}
        aria-invalid={Boolean(error)}
      />
    );
  };

  return (
    <div className="valentine-overlay" role="dialog" aria-modal="true">
      <div className="valentine-modal">
        <Hearts />
        {showConfetti && <Confetti />}
        <div className="valentine-content-relative">
          <div className="valentine-close-wrap">
            <button className="valentine-close" onClick={handleCloseWidget}>✕</button>
            <span className="valentine-close-tooltip">{closeTooltipLabel}</span>
          </div>

          {isPreparing ? (
            <div className="valentine-view valentine-prep">
              <div className="prep-heart">💕</div>
              <h2 className="valentine-prep-title">{prepTitle}</h2>
              <p className="valentine-prep-subtitle">{prepSubtitle}</p>
              <div className="valentine-prep-spinner" aria-hidden="true" />
            </div>
          ) : !result ? (
            <div className="valentine-view intro-view">
              <header className="valentine-header">
                <div className="header-heart">💕</div>
                <h1 className="intro-title">{headlineText}</h1>
                {introSubtitle && <p className="intro-subtitle">{introSubtitle}</p>}
              </header>

              <form className="valentine-form" onSubmit={handleSubmit} noValidate>
                <div className="partners-grid">
                  <div className="partner-card">
                    <div className="partner-icon partner-icon-female" role="img" aria-label={genderFemale}>♀️</div>
                    <div className="input-toggle">
                      <button
                        type="button"
                        className={`toggle-btn ${partner1Mode === "birthdate" ? "is-active" : ""}`}
                        onClick={() => {
                          setPartner1Mode("birthdate");
                          setPartner1Error(null);
                        }}
                      >
                        {modeBirthdateLabel}
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${partner1Mode === "sign" ? "is-active" : ""}`}
                        onClick={() => {
                          setPartner1Mode("sign");
                          setPartner1Error(null);
                        }}
                      >
                        {modeSignLabel}
                      </button>
                    </div>

                    {partner1Mode === "birthdate" ? (
                      <>
                        <label className="input-label">{birthdateLabel}</label>
                        {renderDateInput(partner1Birthdate, setPartner1Birthdate, partner1Error, setPartner1Error)}
                      </>
                    ) : (
                      <>
                        <label className="input-label">{signLabel}</label>
                        <select
                          className="valentine-select"
                          value={partner1Sign}
                          onChange={(e) => {
                            setPartner1Sign(e.target.value);
                            setPartner1Error(null);
                          }}
                        >
                          <option value="">{signPlaceholder}</option>
                          {signOptions.map((sign) => (
                            <option key={sign.id} value={sign.id}>
                              {sign.emoji ? `${sign.emoji} ` : ""}{sign.label}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                    <p className={partner1Error ? "input-error" : "input-hint"}>
                      {partner1Error || (partner1Mode === "birthdate" ? inputHint : "\u00A0")}
                    </p>
                  </div>

                  <div className="partner-divider">💘</div>

                  <div className="partner-card">
                    <div className="partner-icon partner-icon-male" role="img" aria-label={genderMale}>♂️</div>
                    <div className="input-toggle">
                      <button
                        type="button"
                        className={`toggle-btn ${partner2Mode === "birthdate" ? "is-active" : ""}`}
                        onClick={() => {
                          setPartner2Mode("birthdate");
                          setPartner2Error(null);
                        }}
                      >
                        {modeBirthdateLabel}
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${partner2Mode === "sign" ? "is-active" : ""}`}
                        onClick={() => {
                          setPartner2Mode("sign");
                          setPartner2Error(null);
                        }}
                      >
                        {modeSignLabel}
                      </button>
                    </div>

                    {partner2Mode === "birthdate" ? (
                      <>
                        <label className="input-label">{birthdateLabel}</label>
                        {renderDateInput(partner2Birthdate, setPartner2Birthdate, partner2Error, setPartner2Error)}
                      </>
                    ) : (
                      <>
                        <label className="input-label">{signLabel}</label>
                        <select
                          className="valentine-select"
                          value={partner2Sign}
                          onChange={(e) => {
                            setPartner2Sign(e.target.value);
                            setPartner2Error(null);
                          }}
                        >
                          <option value="">{signPlaceholder}</option>
                          {signOptions.map((sign) => (
                            <option key={sign.id} value={sign.id}>
                              {sign.emoji ? `${sign.emoji} ` : ""}{sign.label}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                    <p className={partner2Error ? "input-error" : "input-hint"}>
                      {partner2Error || (partner2Mode === "birthdate" ? inputHint : "\u00A0")}
                    </p>
                  </div>
                </div>

                <button className="valentine-submit" type="submit" disabled={isLoading}>
                  💘 {isLoading ? ctaSubmitLoadingLabel : ctaSubmitLabel}
                </button>
              </form>
            </div>
          ) : (
            <div className="valentine-view result-view">
              <h2 className="result-title">{resultHeadline}</h2>

              <div className="compatibility-header">
                <div className="sign-pair">
                  <div className="sign-badge">{result.partner1_sign.emoji}</div>
                  <span className="sign-label">{result.partner1_sign.label}</span>
                </div>
                <div className="compatibility-score">
                  <div className="score-value">{result.compatibility.compatibility_score}%</div>
                  <div className="score-label">kompatibilita</div>
                </div>
                <div className="sign-pair">
                  <div className="sign-badge">{result.partner2_sign.emoji}</div>
                  <span className="sign-label">{result.partner2_sign.label}</span>
                </div>
              </div>

              <div className="compatibility-title">{result.compatibility.title}</div>

              <div className="result-layout">
                <div className="result-info-card">
                  <p className="compatibility-preface">
                    {previewNote}
                  </p>
                  <p className="compatibility-description">{result.compatibility.description}</p>

                  <div className="info-section">
                    <h4 className="section-label">✅ {benefitsLabel}</h4>
                    <ul className="traits-list strengths">
                      {result.compatibility.strengths.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>

                  <div className="info-section">
                    <h4 className="section-label">⚠️ {challengesLabel}</h4>
                    <ul className="traits-list challenges">
                      {result.compatibility.challenges.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>

                  <div className="love-tip">
                    <h4 className="section-label">💡 {loveTipLabel}</h4>
                    <p>{result.compatibility.love_tip}</p>
                  </div>
                </div>

                <div className="result-actions-col">
                  <div className="email-section">
                    <p className="email-label">📧 {emailLabel}</p>
                    <div className="email-form">
                      <input
                        type="email"
                        placeholder={emailPlaceholder}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="email-input"
                        disabled={emailStatus === "sent"}
                      />
                      <button
                        className="email-submit"
                        onClick={handleSendEmail}
                        disabled={emailStatus === "sending" || emailStatus === "sent" || !email}
                      >
                        {emailStatus === "sending" ? "..." : emailStatus === "sent" ? "✓" : emailSubmitLabel}
                      </button>
                    </div>
                    {emailStatus === "sent" && <p className="email-success">{emailSuccessLabel}</p>}
                    {emailStatus === "error" && <p className="email-error">{emailErrorLabel}</p>}
                  </div>

                  <p className="products-title">{productsLabel}</p>
                  <div className="valentine-products">
                    {result.products?.slice(0, 3).map((p, i) => (
                      <a key={i} href={p.url} target="_blank" className="valentine-product-card" rel="noreferrer">
                        {resolveAssetUrl(p.image) && (
                          <img className="prod-thumb" src={resolveAssetUrl(p.image)} alt={p.title || p.name || "Produkt"} />
                        )}
                        <div>
                          <p className="prod-title">{p.title || p.name}</p>
                          <p className="prod-benefit">{p.benefit}</p>
                        </div>
                      </a>
                    ))}
                  </div>

                  <button
                    className={`valentine-cta-buy ${cartStatus === "added" ? "is-added" : ""}`}
                    onClick={handleBuySet}
                    disabled={isAddingSet || cartStatus === "added"}
                  >
                    {cartStatus === "adding" && <span className="btn-spinner" aria-hidden="true" />}
                    {cartStatus === "adding"
                      ? cartAddingLabel
                      : cartStatus === "added"
                        ? `✓ ${cartAddedLabel}`
                        : `🛒 ${ctaBuyLabel}`}
                  </button>
                  {cartStatus === "added" && (
                    <button className="valentine-open-cart" onClick={handleOpenCart}>
                      {openCartLabel}
                    </button>
                  )}
                  {cartStatus === "error" && <p className="cart-error">{cartErrorLabel}</p>}

                  <div className={`valentine-code-card ${copyState === "copied" ? "is-copied" : ""}`}>
                    <p className="code-label">{couponLabel}</p>
                    <span className="code-value">{result.coupon.code}</span>
                    {couponBenefit && <p className="coupon-benefit">{couponBenefit}</p>}
                  </div>

                  <div className="valentine-share">
                    <span className="share-label">{shareLabel}</span>
                    <div className="share-buttons">
                      <button onClick={() => share("whatsapp")}>WhatsApp</button>
                      <button onClick={() => share("facebook")}>Facebook</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
